/**
 * Small building blocks for the setup forms: a titled section, a styled disclosure (instead of the
 * browser's ▶ triangle) and an inline error that sits next to the thing to fix.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Icon } from '../../components/ui';

export function FormSection({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="setup-section">
      <header className="setup-section__head">
        <span className="eyebrow">{title}</span>
        {aside ? <span className="setup-section__aside">{aside}</span> : null}
      </header>
      <div className="setup-section__body">{children}</div>
    </section>
  );
}

export function Disclosure({ title, hint, children, defaultOpen, className }: { title: string; hint?: ReactNode; children: ReactNode; defaultOpen?: boolean; className?: string }) {
  return (
    <details className={`setup-more${className ? ` ${className}` : ''}`} open={defaultOpen}>
      <summary>
        <span className="setup-more__title">{title}</span>
        {hint ? <span className="setup-more__hint">{hint}</span> : null}
        <Icon name="chevron-down" size={18} className="setup-more__chev" />
      </summary>
      <div className="setup-more__body">{children}</div>
    </details>
  );
}

/** The one validation message, shown where it applies; scrolls itself into view when it appears. */
export function InlineError({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [children]);
  return (
    <div ref={ref} className="field__error setup-error" role="alert">
      {children}
    </div>
  );
}
